import { createFileRoute, Link } from "@tanstack/react-router";
import { verifyEmail, type VerifyEmailError } from "api-client";
import { Button } from "ui";

import { SztamaLogo } from "src/components/SztamaLogo";
import { loaderHandler } from "src/lib/loaderHandler";

export const Route = createFileRoute("/verify-email/$verificationId")({
  component: EmailVerificationPage,
  ssr: true,
  loader: async (loaderOptions) => {
    return loaderHandler(async () => {
      const verificationId = loaderOptions.params.verificationId;

      if (!verificationId) {
        return {
          data: {
            status: "missing_id" as const,
          },
        };
      }

      try {
        const response = await verifyEmail({
          body: { id: verificationId },
        });
        const verification = response.data.data;

        return {
          data: {
            status: "success" as const,
            userId: verification.userId,
            email: verification.email,
          },
        };
      } catch (error) {
        const errorResponse = error as {
          error?: VerifyEmailError;
        };
        const errorCode =
          errorResponse.error?.error.code ??
          errorResponse.error?.error.message ??
          "UNKNOWN_ERROR";

        return { data: { status: "error" as const, errorCode } };
      }
    });
  },
});

function EmailVerificationPage() {
  const loaderData = Route.useLoaderData();
  const data = loaderData?.data;

  if (data?.status === "success") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md text-center">
          <SztamaLogo className="mx-auto mb-6 text-gray-900" />
          <h1 className="mb-4 text-2xl font-bold text-gray-900">
            Email verified!
          </h1>
          <p className="mb-8 text-gray-600">
            Your account is ready. You can now sign in.
          </p>
          <Link to="/sign-in" search={{ email: data.email }}>
            <Button>Sign in</Button>
          </Link>
        </div>
      </div>
    );
  }

  const errorMessages: Record<string, string> = {
    EMAIL_VERIFICATION_INVALID_LINK: "Invalid verification link.",
    invalid: "Invalid verification link.",
    missing_id: "No verification id provided.",
    UNKNOWN_ERROR: "Something went wrong. Please try again.",
  };

  const errorCode = data?.status === "error" ? data.errorCode : "missing_id";
  const errorMessage = errorMessages[errorCode] ?? errorMessages.UNKNOWN_ERROR;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md text-center">
        <div className="mb-6 text-6xl">✗</div>
        <h1 className="mb-4 text-2xl font-bold text-red-600">
          Verification failed
        </h1>
        <p className="mb-8 text-gray-600">{errorMessage}</p>
        <Link to="/sign-up">
          <Button>Back to sign up</Button>
        </Link>
      </div>
    </div>
  );
}
